'use strict';

const { fetchProductBids } = require('./adformBidsRequest');
const adformConstants = require('./adformConstants');
const CATEGORIES_SPONSORED_PRODUCT_ADS_POSITIONS = adformConstants.CATEGORIES_SPONSORED_PRODUCT_ADS_POSITIONS;
const adformElementEvents = require('./adformElementEvents');

const initPopOver = require('../../../../../../app_progamers_core/cartridge/client/default/js/components/initPopOver');
const { productTileTracking } = require('../../../../../../app_progamers_core/cartridge/client/default/js/components/datalayer');

let adformResponseBids = [];

/**
 * Sets tile img attr: loading = eager
 * Sets tile img attr: fetchpriority = high
 * @param {Object} tile - tile Markup
 */
function prioritizeImgLoading(tile) {
    const img = tile.querySelector('.js-image-container img');

    if (img) {
        img.setAttribute('loading', 'eager');
        img.setAttribute('fetchpriority', 'high');
    }
}

/**
 * Generate fresh tracking positions for product tiles in grid.
 * Hint: when adform tiles are added, old positions became invalid and new tiles don't have tracking.
 * @param {Element} productGrid - wrapping element around product tiles
 */
function updatePLPGridProductTilesTrackingPositions(productGrid) {
    if (!productGrid) {
        return;
    }
    const firstTileWithTrackingPosition = productGrid.querySelector('.product-tiles[data-tracking-position]');
    const firstTrackingPosition = parseInt(firstTileWithTrackingPosition?.getAttribute('data-tracking-position'));
    if (!firstTrackingPosition) {
        return;
    }
    productGrid.querySelectorAll('.product-tiles').forEach((tile, index) => {
        tile.setAttribute('data-tracking-position', `${firstTrackingPosition + index}`.toString());
    });
}

/**
 * Fetch products from our BE based on adform bids (brand_id...).
 * On success, add products to grid as ads and init events (in viewport, click...)
 * @param {Object[]} bids - array of bids
 */
function ajaxFetchSponsoredProductAds(bids) {
    if (!bids || !bids.length) {
        return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const currentCatalogPage = parseInt(urlParams.get('page')) || 1;
    if (!adformConstants.CATEGORIES_SPONSORED_PRODUCT_ADS_ON_PAGES.includes(currentCatalogPage)) {
        return;
    }

    const categoryId = window.categoryId;
    const brandIds = bids.map(bid => bid.descriptionAsset.brand_id);
    const firstCategoryIdsPerBid = bids.map(bid => bid.descriptionAsset.category_ids[0]);
    const existingProductIds = [];
    const pidAttribute = 'data-pid';
    document.querySelectorAll(`.js-product-grid [${pidAttribute}]`).forEach(pidDiv => {
        existingProductIds.push(pidDiv.getAttribute(pidAttribute));
    });
    const urlAttribute = 'data-adx-search-page-tiles-action';
    let adxSearchPageTilesActionUrl = document.querySelector(`div[${urlAttribute}]`)?.getAttribute(urlAttribute);
    if (
        !categoryId
        || !brandIds.length
        || !existingProductIds.length
        || !firstCategoryIdsPerBid.length
        || !adxSearchPageTilesActionUrl
    ) {
        return;
    }

    const params = [
        `currentCatalogPage=${currentCatalogPage}`,
        `existingProductIds=${existingProductIds.join('|')}`,
        `brandIds=${brandIds.join('|')}`,
        `categoryId=${categoryId}`,
        `firstCategoryIdsPerBid=${firstCategoryIdsPerBid.join('|')}`
    ];
    adxSearchPageTilesActionUrl += `?${params.join('&')}`;

    fetch(adxSearchPageTilesActionUrl)
        .then((response) => {
            if (response.ok) {
                return response.text();
            }

            return false;
        })
        .then((html) => {
            if (!html) {
                return;
            }

            const parser = new DOMParser();
            const responseDom = parser.parseFromString(html, 'text/html');
            const responseTileWrappers = responseDom.querySelectorAll('.js-adform-product-wrapper');

            if (!responseTileWrappers.length) {
                return;
            }

            const productTileWrappers = document.querySelectorAll('.product-tiles');
            if (!productTileWrappers.length) {
                return;
            }

            responseTileWrappers.forEach((responseTileWrapper, index) => {
                const tilePositionIndex1Notation = CATEGORIES_SPONSORED_PRODUCT_ADS_POSITIONS[index];
                if (!(tilePositionIndex1Notation >= 0)) {
                    return;
                }

                const tilePosition = tilePositionIndex1Notation - 1 - index;
                const siblingProductTileWrapper = productTileWrappers[tilePosition];
                const bidIndex = responseTileWrapper.getAttribute('data-bidIndex');
                const pdpURL = responseTileWrapper.getAttribute('data-pdpURL');
                const bid = bids[bidIndex];

                if (!siblingProductTileWrapper
                    || !bid
                    || !pdpURL
                ) {
                    return;
                }

                if (tilePositionIndex1Notation <= 2) {
                    prioritizeImgLoading(responseTileWrapper);
                }

                const newAdformProductTileWrapper = document.createElement('div');
                newAdformProductTileWrapper.classList.add('product-tiles');

                siblingProductTileWrapper.parentNode.insertBefore(newAdformProductTileWrapper, siblingProductTileWrapper);
                newAdformProductTileWrapper.innerHTML = responseTileWrapper.innerHTML;
                document.body.dispatchEvent(new CustomEvent('product:tileRendered', { detail: newAdformProductTileWrapper, bubbles: true }));
                adformElementEvents.initSponsoredProductAdInViewportObserver(newAdformProductTileWrapper, bid);
                adformElementEvents.initSponsoredProductAdClickEvent(newAdformProductTileWrapper, bid, pdpURL);
            });

            // todo: move "js product init" logic to shared place and reuse in search.js too
            initPopOver();

            const productGrid = document.querySelector('.js-product-grid');
            updatePLPGridProductTilesTrackingPositions(productGrid);
            productTileTracking(productGrid);
        });
}

/**
 * Init events that wait for adform response to get processed:
 * - case bids exist => call our BE to return tiles for grid (search based on adform response)
 * - case no bids => nothing happens
 */
function initResponseHandlingEvents() {
    document.addEventListener(adformConstants.PRODUCT_BIDS_READY_CUSTOM_EVENT_NAME, function (e) {
        adformResponseBids = e.detail?.bids;
        ajaxFetchSponsoredProductAds(adformResponseBids);
    });

    document.addEventListener(adformConstants.PRODUCT_NO_BIDS_CUSTOM_EVENT_NAME, function () {});
}

/**
 * Init events that make adform request to get data needed for products tiles search.
 */
function initMakeRequestEvents() {
    document.addEventListener('isml:searchResults:ready', function () {
        // note:
        // in this event we are safe to querySelector plp elements
        // doing it outside may lead to null due to race conditions (adform code is running before page is loaded)

        const productTileWrappers = document.querySelectorAll('.product-tiles');
        const areFiltersApplied = !!(document.querySelector('.js-search-results .js-search-reset-btn'));
        if (!productTileWrappers.length || areFiltersApplied) {
            return;
        }

        fetchProductBids();
    });
}

/**
 * Init adform fetch request and handling response (bids or no bids).
 *
 * Important:
 * initResponseHandlingEvents() must be called before initMakeRequestEvents()
 * => otherwise request could dispatch event (e.g. early return "no-bids" event when request not valid)
 *    that can't be processed (response handler event not initialized yet)
 */
function initRequestLifecycleEvents() {
    initResponseHandlingEvents();
    initMakeRequestEvents();
}

/**
 * Init events related to product ads (when to call adform, event when adform returns bids...).
 */
function initEvents() {
    initRequestLifecycleEvents();

    document.addEventListener('search:filtered', function () {
        adformResponseBids = [];
    });

    document.addEventListener('search:pagination-changed', function () {
        ajaxFetchSponsoredProductAds(adformResponseBids);
    });
}

module.exports = {
    init: function () {
        if (!window.shouldFetchAdformCategoryProductsSponsoredAds
            || !window.categoryId
            || CATEGORIES_SPONSORED_PRODUCT_ADS_POSITIONS.length === 0
        ) {
            return;
        }

        initEvents();
    }
};
